/**
 * Hardware profile schema.
 *
 * Design constraints driven by the spec:
 *  - Multidimensional fingerprints, not "warmth = +3" (spec 6).
 *  - Every parameter measurement-upgradeable without touching the DSP (spec 7).
 *  - Provenance on every quantitative claim (spec 8, 51).
 *  - Versioned (spec 44).
 *  - Registry-scalable to 10k+ entries (spec 13, 84).
 */

import type {
  ChannelBehaviour,
  DirectivityData,
  FRCurve,
  HarmonicSpectrum,
  ImpedanceCurve,
  IRSource,
  MeasurementRef,
  NoiseProfile,
  PhaseCurve,
} from './curves';
import type { Confidence } from './provenance';

/**
 * The category list is derived from this array rather than the reverse, so that
 * adding a category to the union forces a compile error anywhere that enumerates
 * them. Cables were added later than the original four, and a hand-maintained
 * copy of this list in the registry silently dropped them from every
 * category-indexed lookup -- exactly the failure this ordering prevents.
 */
export const HARDWARE_CATEGORIES = ['dac', 'amp', 'speaker', 'headphone', 'cable'] as const;
export type HardwareCategory = (typeof HARDWARE_CATEGORIES)[number];

export type PriceStatus =
  | 'MSRP'
  | 'ORIGINAL_RETAIL'
  | 'CURRENT_RETAIL'
  | 'APPROXIMATE'
  | 'DISCONTINUED_USED'
  | 'UNKNOWN';

export interface PriceInfo {
  /** Numeric amount in `currency`. */
  amount: number;
  currency: 'USD' | 'EUR' | 'GBP' | 'JPY';
  status: PriceStatus;
  /** Year the price applies to. */
  year?: number;
  note?: string;
}

/** Common header shared by every profile in every category. */
export interface ProfileMeta {
  /** Stable unique id, e.g. 'dac.chord.dave'. */
  id: string;
  category: HardwareCategory;
  manufacturer: string;
  model: string;
  /** Optional short display name when model strings get long. */
  shortName?: string;
  price: PriceInfo;
  /** Profile schema/data version (spec 44). */
  version: number;
  /** ISO date this profile revision was authored. */
  revised: string;
  /** Where the data behind this profile came from. */
  provenanceNote?: string;
  measurement?: MeasurementRef;
  /** Overall author-declared confidence; UI also computes an aggregate. */
  confidence: Confidence;
  /** Free-text engineering notes shown in the inspector. */
  notes?: string;
  /** Search/filter tags. */
  tags?: string[];
  /** Year of introduction. */
  year?: number;
  countryOfOrigin?: string;
  /** Image slot: never a hot-linked copyrighted asset (spec 97). */
  image?: ProfileImage;
  /** Marks the bundled worked example (spec 85). */
  isExample?: boolean;
  /** True for user-imported profiles. */
  userDefined?: boolean;
}

export interface ProfileImage {
  /** 'generated' draws a vector illustration from `seed` + category. */
  kind: 'generated' | 'url' | 'dataurl';
  seed?: string;
  url?: string;
  /** Dominant accent used by the generated illustration. */
  accent?: string;
  /** Attribution/licence when a real image is later supplied. */
  credit?: string;
}

// ===========================================================================
// DAC
// ===========================================================================

export type DacArchitecture =
  | 'delta-sigma'
  | 'multibit-delta-sigma'
  | 'r2r'
  | 'discrete-r2r'
  | 'ladder-multibit'
  | 'fpga-pulse-array'
  | 'fpga-custom'
  | 'hybrid'
  | 'segmented';

export type DacOutputStage =
  | 'op-amp'
  | 'discrete-class-a'
  | 'tube'
  | 'transformer'
  | 'passive-iv'
  | 'jfet'
  | 'hybrid';

export type ReconstructionMode =
  | 'linear-phase'
  | 'minimum-phase'
  | 'hybrid'
  | 'apodizing'
  | 'nos'
  | 'slow-linear'
  | 'slow-minimum'
  | 'brickwall'
  | 'custom-fir';

export interface DigitalFilterOption {
  id: string;
  label: string;
  mode: ReconstructionMode;
  /** Transition band start as fraction of Nyquist (0..1). */
  cutoffRatio?: number;
  /** Stopband attenuation, dB. */
  stopbandDb?: number;
  /** Transition width as fraction of Nyquist. Wider = slower roll-off. */
  transitionRatio?: number;
  /** Taps for the modelled FIR. Higher = longer pre/post ringing. */
  taps?: number;
  /** Explicit FIR coefficients if the real filter is known. */
  fir?: number[];
  /** For NOS: order of the zero-order-hold sinc droop compensation. */
  nosHoldOrder?: number;
  confidence?: Confidence;
}

export interface DacConversionModel {
  architecture: DacArchitecture;
  /** Chipset / core description, e.g. 'ESS ES9038PRO', 'Discrete 26-bit R2R'. */
  core: string;
  /** Effective resolution in bits, drives the quantisation/idle noise model. */
  effectiveBits: number;
  /** Number of parallel converter channels summed per output. */
  parallelUnits?: number;
  /** Internal oversampling ratio the hardware itself runs at. */
  internalOversampling?: number;
  /**
   * R-2R ladder element mismatch, expressed in LSB. Produces level-dependent
   * (small-signal) nonlinearity -- the classic ladder INL signature.
   */
  ladderMismatchLsb?: number;
  /** Sigma-delta idle-tone tendency, 0..1. */
  idleToneLevel?: number;
  /** Noise shaper order for delta-sigma cores. */
  noiseShaperOrder?: number;
  /** Clock jitter, picoseconds RMS. */
  jitterPsRms?: number;
  /** Jitter spectrum: 'random' | 'periodic' with a dominant rate. */
  jitterKind?: 'random' | 'periodic' | 'mixed';
  jitterToneHz?: number;
  confidence?: Confidence;
}

export interface AnalogOutputModel {
  stage: DacOutputStage;
  /** Unbalanced output level at 0 dBFS, Vrms. */
  outputVrmsSE?: number;
  /** Balanced output level at 0 dBFS, Vrms. */
  outputVrmsBal?: number;
  outputImpedanceOhms?: number;
  balancedAvailable?: boolean;
  /** Analog-domain frequency response deviation. */
  response?: FRCurve;
  phase?: PhaseCurve;
  /** Slew limit in V/us; below this the stage soft-limits transients. */
  slewRateVus?: number;
  confidence?: Confidence;
}

/**
 * Nonlinear model descriptor. This is a tagged union consumed verbatim by the
 * worklets, so adding a new kind means adding one case in the DSP, not a new
 * profile schema.
 */
export type NonlinearKind =
  | 'none'
  | 'polynomial'
  | 'tanh'
  | 'asym-tanh'
  | 'triode'
  | 'pentode'
  | 'class-b-crossover'
  | 'class-d-pwm'
  | 'transformer-core'
  | 'r2r-inl'
  | 'sigma-delta'
  | 'jfet-square'
  | 'electrostatic'
  | 'planar'
  | 'dynamic-driver'
  | 'custom-curve';

export interface NonlinearModel {
  kind: NonlinearKind;
  /** Drive level normalisation: input is divided by this before shaping. */
  headroomDb?: number;
  /** Blend with the dry signal, 0..1 (1 = fully shaped). */
  amount?: number;
  /** Even/odd harmonic bias. -1 = odd only, +1 = even only, 0 = balanced. */
  bias?: number;
  /** Asymmetry of the transfer curve, -1..1. */
  asymmetry?: number;
  /** Polynomial coefficients (a1..aN) when kind = 'polynomial'. */
  coeffs?: number[];
  /** Explicit transfer curve samples over [-1, 1] when kind = 'custom-curve'. */
  curve?: number[];
  /** Ladder INL amplitude in LSB when kind = 'r2r-inl'. */
  inlLsb?: number;
  /** Class-D carrier frequency, Hz. */
  carrierHz?: number;
  /** Crossover notch width for class-B, in normalised units. */
  crossoverWidth?: number;
  /** Transformer core saturation knee (normalised) and hysteresis amount. */
  coreKnee?: number;
  hysteresis?: number;
  /** Oversampling factor to run this nonlinearity at (anti-alias). */
  oversample?: 1 | 2 | 4 | 8;
  confidence?: Confidence;
}

export interface TimeDomainModel {
  /** Explicit impulse response of the whole device, if measured. */
  ir?: IRSource;
  /** Pre-ringing amount 0..1 relative to the modelled reconstruction filter. */
  preRing?: number;
  postRing?: number;
  /** Group-delay ripple in samples. */
  groupDelayRippleSamples?: number;
}

export interface DacProfile extends ProfileMeta {
  category: 'dac';
  conversion: DacConversionModel;
  /** Selectable reconstruction filters; index 0 is the default. */
  filters: DigitalFilterOption[];
  defaultFilterId?: string;
  analog: AnalogOutputModel;
  nonlinear: NonlinearModel;
  harmonics: HarmonicSpectrum;
  noise: NoiseProfile;
  channels: ChannelBehaviour;
  time?: TimeDomainModel;
  /** Supported input rates, Hz. */
  sampleRates: number[];
  maxBitDepth: number;
  dsdSupport?: 'none' | 'dsd64' | 'dsd128' | 'dsd256' | 'dsd512' | 'dsd1024';
  /**
   * Sample-rate-dependent response shaping: response deviation applied per
   * input family (44.1k multiples vs 48k multiples vs high-rate).
   */
  rateDependentResponse?: Record<string, FRCurve>;
  dynamicRangeDb?: number;
  thdPlusNDb?: number;
}

// ===========================================================================
// AMPLIFIER
// ===========================================================================

export type AmpTopology =
  | 'solid-state-ab'
  | 'solid-state-a'
  | 'class-d'
  | 'class-g'
  | 'class-h'
  | 'tube-push-pull'
  | 'tube-set'
  | 'tube-otl'
  | 'hybrid'
  | 'transformer-coupled';

export type AmpForm =
  | 'integrated'
  | 'stereo-power'
  | 'monoblock'
  | 'preamp'
  | 'headphone'
  | 'electrostatic-energiser';

export interface AmpPowerSpec {
  /** Watts into 8 ohms. */
  watts8?: number;
  watts4?: number;
  watts2?: number;
  /** Headphone amps: mW into 32/300/600 ohms. */
  mw32?: number;
  mw300?: number;
  mw600?: number;
  /** Electrostatic energisers: bias volts and output Vrms. */
  biasVolts?: number;
  outputVrms?: number;
  confidence?: Confidence;
}

export interface AmpOutputModel {
  /** Output impedance in ohms (damping factor = nominal load / this). */
  outputImpedanceOhms: number;
  /** Frequency dependence of output impedance. */
  outputImpedanceCurve?: FRCurve;
  /** Global negative feedback in dB. Low feedback = more load interaction. */
  feedbackDb?: number;
  /** Output transformer present (tube amps) -- adds LF/HF rolloff + core sat. */
  outputTransformer?: boolean;
  transformerLfHz?: number;
  transformerHfHz?: number;
  /** Damping factor referenced to 8 ohms, if quoted rather than derived. */
  dampingFactor?: number;
}

export interface AmpDynamicsModel {
  /** Supply sag: dB of gain loss at sustained full power. */
  sagDb?: number;
  /** Sag time constant, ms. */
  sagTimeMs?: number;
  /** Recovery time constant, ms. */
  recoveryMs?: number;
  /** Thermal/bias drift compression, dB over seconds. */
  thermalDb?: number;
  thermalTimeMs?: number;
  /** Slew rate limit, V/us. */
  slewRateVus?: number;
  /** Peak/headroom above rated continuous power, dB. */
  headroomDb?: number;
}

export interface AmpClippingModel {
  /** 'soft' | 'hard' | 'asymmetric'. */
  kind: 'soft' | 'hard' | 'asymmetric';
  /** Knee width in dB below the clip point. */
  kneeDb: number;
  /** Recovery behaviour after clipping: 0 = instant, 1 = sticky. */
  stickiness?: number;
  /** dBFS at which clipping begins for a nominal listening gain. */
  onsetDbFS?: number;
}

export interface AmpProfile extends ProfileMeta {
  category: 'amp';
  topology: AmpTopology;
  form: AmpForm;
  /** Tube complement / output devices, e.g. '300B SET', 'Hypex NC500'. */
  devices?: string;
  power: AmpPowerSpec;
  /** Voltage gain in dB. */
  gainDb: number;
  response: FRCurve;
  phase?: PhaseCurve;
  output: AmpOutputModel;
  nonlinear: NonlinearModel;
  harmonics: HarmonicSpectrum;
  noise: NoiseProfile;
  channels: ChannelBehaviour;
  dynamics: AmpDynamicsModel;
  clipping: AmpClippingModel;
  time?: TimeDomainModel;
  /** Inputs the amp accepts. */
  balancedInput?: boolean;
  /** Does this amp drive electrostatic headphones? */
  electrostaticDrive?: boolean;
  /** Compatible bias standard for electrostatic energisers. */
  estatBias?: 'pro-580v' | 'normal-230v' | 'none';
  thdPercent?: number;
  snrDb?: number;
}

// ===========================================================================
// SPEAKER / HEADPHONE
// ===========================================================================

export type SpeakerType =
  | 'floorstander'
  | 'standmount'
  | 'horn'
  | 'electrostatic'
  | 'planar-magnetic'
  | 'coaxial'
  | 'active'
  | 'omnidirectional'
  | 'dipole'
  | 'line-source'
  | 'open-baffle';

export type EnclosureType =
  | 'sealed'
  | 'ported'
  | 'passive-radiator'
  | 'transmission-line'
  | 'open-baffle'
  | 'horn-loaded'
  | 'dipole-panel'
  | 'infinite-baffle';

export type DriverTech =
  | 'dynamic'
  | 'beryllium-dome'
  | 'diamond-dome'
  | 'ribbon'
  | 'amt'
  | 'electrostatic-panel'
  | 'planar-panel'
  | 'compression-driver'
  | 'plasma'
  | 'bending-wave'
  | 'coaxial-point-source'
  | 'field-coil';

export interface CrossoverSection {
  /** Crossover frequency, Hz. */
  freqHz: number;
  /** Acoustic slope in dB/octave. */
  slopeDbOct: number;
  /** Alignment shapes the phase behaviour through the crossover. */
  alignment?: 'butterworth' | 'linkwitz-riley' | 'bessel' | 'first-order' | 'digital-lr';
  /** Relative polarity of the upper section. */
  invertUpper?: boolean;
  /** Physical offset between drivers in mm -> arrival-time error. */
  offsetMm?: number;
}

export interface CabinetModel {
  enclosure: EnclosureType;
  /** Port tuning frequency, Hz. */
  portTuningHz?: number;
  /** Port compression/chuffing onset, 0..1. */
  portCompression?: number;
  /** Port organ-pipe resonance, Hz. */
  portPipeHz?: number;
  /** Cabinet wall resonances: [freqHz, levelDb, Q]. */
  resonances?: Array<[number, number, number]>;
  /** System Q for sealed alignments. */
  qtc?: number;
  /** -3 dB and -6 dB points, Hz. */
  f3Hz?: number;
  f6Hz?: number;
  /** High-pass order used to model the low-frequency rolloff. */
  rolloffOrder?: 2 | 4;
}

export interface DirectivityModel {
  /** Measured/imported data if available. */
  data?: DirectivityData;
  /**
   * Parameterised fallback (explicitly MODELLED, spec 20). Frequency above
   * which the source narrows, and how fast, plus the horizontal coverage angle.
   */
  coverageAngleDeg?: number;
  narrowingStartHz?: number;
  narrowingSlopeDbOct?: number;
  /** Dipole/omni radiation pattern order: 0 = omni, 1 = dipole/cardioid. */
  patternOrder?: number;
  /** Baffle step frequency, Hz, and depth in dB. */
  baffleStepHz?: number;
  baffleStepDb?: number;
  confidence?: Confidence;
}

export interface SpeakerDistortionModel {
  /** Harmonic structure at a reference SPL. */
  harmonics: HarmonicSpectrum;
  /** SPL at which `harmonics` applies. */
  refSpl?: number;
  /** Excursion-limited LF distortion: below this Hz, distortion rises fast. */
  excursionKneeHz?: number;
  excursionRiseDbOct?: number;
  /** Power compression at high level, dB. */
  compressionDb?: number;
}

export interface SpeakerProfile extends ProfileMeta {
  category: 'speaker';
  speakerType: SpeakerType;
  drivers: DriverTech[];
  driverCount?: number;
  wayCount?: number;
  active?: boolean;
  /** dB / 2.83 V / 1 m. */
  sensitivityDb: number;
  impedance: ImpedanceCurve;
  /** On-axis anechoic response. */
  response: FRCurve;
  /** Estimated in-room / listening-window response if known separately. */
  listeningWindow?: FRCurve;
  soundPower?: FRCurve;
  phase?: PhaseCurve;
  crossover: CrossoverSection[];
  cabinet: CabinetModel;
  directivity: DirectivityModel;
  distortion: SpeakerDistortionModel;
  nonlinear: NonlinearModel;
  time?: TimeDomainModel;
  /** Recommended amplifier power, W. */
  recommendedWatts?: [number, number];
  /** Physical size in mm [H, W, D] and mass in kg. */
  dimensionsMm?: [number, number, number];
  massKg?: number;
  /** How much the design depends on room loading. */
  roomCoupling?: 'low' | 'medium' | 'high';
}

export type HeadphoneType =
  | 'electrostatic'
  | 'planar-magnetic'
  | 'dynamic'
  | 'bone-conduction'
  | 'air-motion';

export type HeadphoneFit = 'open' | 'semi-open' | 'closed';

export interface HeadphoneProfile extends ProfileMeta {
  category: 'headphone';
  headphoneType: HeadphoneType;
  fit: HeadphoneFit;
  /** Raw response measured on a coupler, before any target compensation. */
  response: FRCurve;
  /** The compensation curve this response is referenced to. */
  target?: {
    name: string;
    curve: FRCurve;
    confidence?: Confidence;
  };
  phase?: PhaseCurve;
  /** Electrostatic: bias voltage; dynamic/planar: impedance. */
  biasVolts?: number;
  impedanceOhms?: number;
  capacitancePf?: number;
  impedance?: ImpedanceCurve;
  sensitivityDbVrms?: number;
  distortion: SpeakerDistortionModel;
  nonlinear: NonlinearModel;
  channels: ChannelBehaviour;
  time?: TimeDomainModel;
  /** How much seal/pad condition shifts the bass response, dB. */
  sealSensitivityDb?: number;
  /** Pad/earcup acoustic volume resonance. */
  cupResonanceHz?: number;
  /** Requires a dedicated energiser rather than a normal headphone amp. */
  requiresEnergiser?: boolean;
  estatBias?: 'pro-580v' | 'normal-230v';
  /** Default crossfeed suggestion for this headphone. */
  suggestedCrossfeed?: 'off' | 'low' | 'medium' | 'high';
}

// ===========================================================================
// CABLE
// ===========================================================================

/**
 * Cables.
 *
 * READ THIS BEFORE ADDING A MODEL. Cables are the most contested subject in
 * audio, and this schema is built so the engine can only express effects it can
 * derive from physics.
 *
 * A passive cable has three electrical properties that matter: series
 * resistance, series inductance and shunt capacitance. Every audible effect a
 * cable can have on an analog signal follows from those three numbers
 * interacting with the impedances at each end. Nothing else is modelled,
 * because nothing else has a mechanism.
 *
 * What that means per type:
 *
 *   SPEAKER      Real and computable. Series R and L sit between the amplifier
 *                and a load whose impedance swings with frequency, forming a
 *                divider. This is the same physics as amplifier output
 *                impedance, which the engine already models, and on a long run
 *                into a 3 ohm minimum it is genuinely measurable.
 *
 *   INTERCONNECT Real and computable, and almost always negligible. Cable
 *                capacitance against the source output impedance is a low-pass
 *                whose corner normally sits hundreds of kHz above the audio
 *                band. The engine computes it and usually reports about
 *                0.00 dB, which is the correct answer rather than a
 *                disappointing one.
 *
 *   POWER        No signal-path effect is applied. A mains cord sits upstream
 *                of the rectifier, regulation and reservoir capacitance of the
 *                component it feeds, and there is no established mechanism by
 *                which it alters the audio signal. Inventing one to make the
 *                feature feel substantial would be exactly the fabrication the
 *                rest of this database refuses.
 *
 *   USB
 *   ETHERNET     No signal-path effect is applied. Both are packetised and
 *                error-checked: the data arrives intact or the link fails.
 *                There is no analog waveform in flight to colour.
 */

export type CableType = 'power' | 'interconnect' | 'usb' | 'ethernet' | 'speaker';

export type ConductorMaterial =
  | 'ofc'
  | 'occ-copper'
  | 'silver-plated-copper'
  | 'pure-silver'
  | 'silver-gold-alloy'
  | 'copper-silver-hybrid'
  | 'carbon'
  | 'copper-clad-aluminium';

export type CableGeometry =
  | 'coaxial'
  | 'twisted-pair'
  | 'star-quad'
  | 'litz'
  | 'braided'
  | 'ribbon'
  | 'flat-parallel'
  | 'solid-core'
  | 'air-tube'
  | 'concentric'
  | 'shotgun';

/**
 * Per-metre electrical characteristics. The only cable properties the DSP reads.
 */
export interface CableElectrical {
  /** LOOP resistance per metre in ohms: both conductors, i.e. what the amplifier sees. */
  resistanceOhmPerM: number;
  /** Series loop inductance per metre, microhenries. */
  inductanceUhPerM: number;
  /** Shunt capacitance per metre, picofarads. */
  capacitancePfPerM: number;
  /** Characteristic impedance for controlled-impedance types (75 ohm coax, 100 ohm Cat-x, 90 ohm USB). */
  characteristicOhms?: number;
  confidence?: Confidence;
  source?: string;
}

export interface CableProfile extends ProfileMeta {
  category: 'cable';
  cableType: CableType;
  conductor: ConductorMaterial;
  geometry: CableGeometry;
  /** AWG of the signal conductors, where the concept applies. */
  gauge?: number;
  electrical: CableElectrical;
  shielding?: string;
  dielectric?: string;
  termination?: string[];
  /** Lengths the manufacturer actually offers, metres. */
  standardLengthsM?: number[];
  /** Length used when the cable is first selected, metres. */
  defaultLengthM: number;
  /** The length the quoted price applies to. Longer runs cost more; not modelled. */
  priceLengthM?: number;
  balancedAvailable?: boolean;
  /** Power cables: current rating in amps. */
  currentRatingA?: number;
  /** USB / Ethernet: the standard the cable claims to meet. */
  digitalStandard?: string;
}

/**
 * Does this cable type have a mechanism by which it can alter the audio signal?
 *
 * The single source of truth for the rule above. The compiler, the inspector
 * and the tests all read it, so what the engine does and what the interface
 * claims cannot drift apart.
 */
export function cableAffectsSignalPath(type: CableType): boolean {
  return type === 'speaker' || type === 'interconnect';
}

export const CABLE_TYPE_LABEL: Record<CableType, string> = {
  power: 'Power cord',
  interconnect: 'Interconnect',
  usb: 'USB',
  ethernet: 'Ethernet',
  speaker: 'Speaker cable',
};

/** Why a cable type is or is not modelled. Shown verbatim in the inspector. */
export const CABLE_TYPE_RATIONALE: Record<CableType, string> = {
  speaker:
    'Modelled. Series resistance and inductance sit between the amplifier and a load whose impedance varies with frequency, forming a divider. This is the same physics as amplifier output impedance and it is genuinely measurable, especially on long runs into low-impedance loads.',
  interconnect:
    'Modelled. Cable capacitance against the source output impedance forms a low-pass filter. The corner normally sits far above the audio band, so the computed in-band effect is usually a small fraction of a decibel. The engine reports whatever it actually works out to.',
  power:
    'Not modelled: no signal-path effect is applied. A mains cord sits upstream of the rectifier, regulation and reservoir capacitance of the component it feeds, and there is no established mechanism by which it alters the audio signal. Rather than invent one, the engine applies identity here and says so. The cord, its gauge, its length and its price are real; the DSP contribution is exactly zero.',
  usb:
    'Not modelled: no signal-path effect is applied. USB audio is packetised and error-checked -- data arrives intact or the transfer fails, and there is no analog waveform in flight to colour. In an asynchronous USB DAC the clock is generated at the DAC, not carried by the cable.',
  ethernet:
    'Not modelled: no signal-path effect is applied. Ethernet is packetised, checksummed and retransmitted on error, and is normally buffered by seconds of playback queue. There is no analog signal path between the two ends.',
};

export type AnyProfile = DacProfile | AmpProfile | SpeakerProfile | HeadphoneProfile | CableProfile;

export type ProfileOf<C extends HardwareCategory> = C extends 'dac'
  ? DacProfile
  : C extends 'amp'
    ? AmpProfile
    : C extends 'speaker'
      ? SpeakerProfile
      : C extends 'cable'
        ? CableProfile
        : HeadphoneProfile;

// ---------------------------------------------------------------------------
// Type guards
// ---------------------------------------------------------------------------

export const isDac = (p: AnyProfile): p is DacProfile => p.category === 'dac';
export const isAmp = (p: AnyProfile): p is AmpProfile => p.category === 'amp';
export const isSpeaker = (p: AnyProfile): p is SpeakerProfile => p.category === 'speaker';
export const isHeadphone = (p: AnyProfile): p is HeadphoneProfile => p.category === 'headphone';
export const isCable = (p: AnyProfile): p is CableProfile => p.category === 'cable';

/** Output-stage profiles: speakers and headphones share the OUTPUT slot. */
export type OutputProfile = SpeakerProfile | HeadphoneProfile;
